import {
  GridApiPro,
  GridColumnVisibilityModel,
  GridHeaderSelectionCheckboxParams,
  GridRowModel,
  GridRowSelectionModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro'
import React, {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { useSelector } from 'react-redux'
import { DataGridHeaderStyle } from 'src/components/DataGrid/DataGrid'
import { TransactionReportType } from 'src/components/ReportTransactionsBoxAndLinks/reportTypes'
import TableEmptyState, {
  TableEmptyStateDesigns,
} from 'src/components/TableEmptyState/TableEmptyState'
import TransactionsDataGrid, {
  TransactionsTableLargeDesigns,
} from 'src/components/TransactionsDataGrid/TransactionsDataGrid'
import { TransactionsFiltersBody } from 'src/components/TransactionsListFilter/transactionsFilter.schema'
import TransactionsTableBulkFloatingToolbar from 'src/components/TransactionsTableBulkFloatingToolbar/TransactionsTableBulkFloatingToolbar'
import TransactionsTableNeedsApprovalBulkFloatingToolbar from 'src/components/TransactionsTableNeedsApprovalBulkFloatingToolbar/TransactionsTableNeedsApprovalBulkFloatingToolbar'
import { useBackgroundDispatch } from 'src/hooks'
import {
  TransactionListModel,
  TransactionSort,
} from 'src/models/transaction.models'
import { useQueryExpenseCategories } from 'src/queries/organizations/expenseCategories.queries'
import { useQueryOwnUser } from 'src/queries/users.queries'
import { createSetIsSyncTransactionsPopupOpen } from 'src/redux/actions/app.actions'
import { updateSelectedTransactions } from 'src/redux/actions/data/transactions.actions'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import { selectSelectedTransactionIdsFromState } from 'src/redux/selectors/data/transactions.selectors'
import { InfiniteScrollProps } from 'src/types'

export interface TransactionListCheckboxProps {
  canSelectTransaction: (transaction: TransactionListModel) => boolean
  handleCheckboxClick?: (selection: GridRowSelectionModel) => void
  handleHeaderCheckboxClick?: (
    params: GridHeaderSelectionCheckboxParams,
  ) => void
}

interface FullTransactionsTableProps {
  className?: string
  transactions: TransactionListModel[]
  fullyLoadedTransactions: boolean
  emptyStateDesign: TableEmptyStateDesigns
  design: TransactionsTableLargeDesigns
  /** Forwarded untouched to the grid. See `TransactionsDataGrid`'s own prop. */
  headerStyle?: DataGridHeaderStyle
  defaultColumnVisibilityModel: GridColumnVisibilityModel
  infiniteScrollProps?: InfiniteScrollProps<TransactionsFiltersBody>
  filters?: TransactionsFiltersBody
  vcRefFields?: boolean
  reportId?: string
  clientHeight: number
  setSort?: (sort: TransactionSort) => void
  preventBulkActions?: boolean
  rowHoverActions?: (params: GridRowModel) => React.ReactElement
  rowApprovalActions?: (params: GridRowModel) => React.ReactElement
  context: TransactionReportType
  apiRef?: MutableRefObject<GridApiPro | null>
  savedVisibleColumns?: string[]
  onRegisterResetColumnsToDefault?: (reset: () => void) => void
  onUserChangedColumns?: () => void
}

const FullTransactionsTable: React.FC<FullTransactionsTableProps> = ({
  context = TransactionReportType.Other,
  ...props
}) => {
  const internalApiRef = useGridApiRef()
  const apiRef = props.apiRef ?? internalApiRef
  const { data: user } = useQueryOwnUser({})
  const { data: expenseDetails } = useQueryExpenseCategories({ filters: {} })
  const activeIntegration = useSelector(selectActiveOrgIntegration)

  const isEmpty =
    props.transactions.length === 0 && props.fullyLoadedTransactions
  const selectedTransactionIds = useSelector(
    selectSelectedTransactionIdsFromState,
  )
  const selectedTransactions = useMemo(
    () =>
      props.transactions.filter((transaction) =>
        selectedTransactionIds.has(transaction.id),
      ),
    [props.transactions, selectedTransactionIds],
  )
  const rowSelectionModel = useMemo<GridRowSelectionModel>(
    () => ({
      type: 'include',
      ids: new Set(selectedTransactionIds),
    }),
    [selectedTransactionIds],
  )

  const dispatch = useBackgroundDispatch()
  const dispatchRef = useRef(dispatch)

  dispatchRef.current = dispatch

  // clear selected transactions on unmount
  useEffect(() => {
    return () => {
      dispatchRef.current(updateSelectedTransactions())
    }
  }, [])

  const canSelectTransaction = useCallback(
    (transaction: TransactionListModel): boolean => {
      // 4 possible bulk actions: manager approve/reject, bulk edit exp cats, bulk edit status, and sync to ERP

      const canBulkEditExpCats =
        !transaction.isSplit &&
        transaction.notPushedToConnectedPlatform(
          activeIntegration?.connectedPlatform,
        )
      const canSync = transaction.canPushToConnectedPlatform(
        activeIntegration?.connectedPlatform,
        undefined,
      )
      // all transactions can have their status modified (limited to specific pages)
      const canBulkEditStatus = true
      // all transactions can be approved/rejected by a manager (limited to a specific page)
      const canManagerApproveOrReject = true

      if ([TransactionReportType.NeedsApproval].includes(context)) {
        // This page only allows manager approve/reject actions
        return canManagerApproveOrReject
      }

      if ([TransactionReportType.AccountingTransactions].includes(context)) {
        // This page allow bulk editing of expense categories and status, and syncing
        return canBulkEditExpCats || canBulkEditStatus || canSync
      }

      if (
        [
          TransactionReportType.MyTransactions,
          TransactionReportType.Account,
          TransactionReportType.Budget,
        ].includes(context)
      ) {
        // These pages only allow bulk editing of expense categories
        return canBulkEditExpCats
      }

      if ([TransactionReportType.Expenses].includes(context)) {
        // This page allows bulk editing of expense categories and bulk syncing
        return canBulkEditExpCats || canSync
      }

      if (
        [
          TransactionReportType.Other,
          TransactionReportType.ExpenseReport,
        ].includes(context)
      ) {
        // These pages offer no bulk actions
        return false
      }

      return false
    },
    [activeIntegration?.connectedPlatform, context],
  )

  const handleCheckboxClick = useCallback(
    (selection: GridRowSelectionModel): void => {
      const nextIds = new Set(selection.ids)

      if (
        nextIds.size === selectedTransactionIds.size &&
        Array.from(nextIds).every((id) => selectedTransactionIds.has(id))
      ) {
        return
      }

      dispatch(updateSelectedTransactions(nextIds))
    },
    [dispatch, selectedTransactionIds],
  )

  const handleCloseToolbar = useCallback((): void => {
    dispatch(updateSelectedTransactions())
  }, [dispatch])

  const checkboxProps: TransactionListCheckboxProps | undefined =
    useMemo(() => {
      const hasExpenseDetails =
        expenseDetails !== undefined &&
        expenseDetails.length > 0 &&
        context !== TransactionReportType.NeedsApproval

      const isReportType = [
        TransactionReportType.ExpenseReport,
        TransactionReportType.AccountingTransactions,
        TransactionReportType.NeedsApproval,
      ].includes(context)

      const hasIntegrationWithPermission =
        activeIntegration?.connectedPlatform !== undefined &&
        user?.hasOrgBookkeeperPermissions

      const canShowCheckbox =
        !props.reportId &&
        !props.preventBulkActions &&
        (hasExpenseDetails || isReportType || hasIntegrationWithPermission)

      return canShowCheckbox
        ? {
            canSelectTransaction,
            handleCheckboxClick,
          }
        : undefined
    }, [
      expenseDetails,
      context,
      activeIntegration?.connectedPlatform,
      user?.hasOrgBookkeeperPermissions,
      props.reportId,
      props.preventBulkActions,
      canSelectTransaction,
      handleCheckboxClick,
    ])

  return (
    <div>
      {isEmpty ? (
        <TableEmptyState design={props.emptyStateDesign} />
      ) : (
        props.infiniteScrollProps &&
        props.filters && (
          <TransactionsDataGrid
            apiRef={apiRef}
            transactions={props.transactions}
            infiniteScrollProps={props.infiniteScrollProps}
            design={props.design}
            headerStyle={props.headerStyle}
            filters={props.filters}
            rowSelectionModel={rowSelectionModel}
            savedVisibleColumns={props.savedVisibleColumns}
            onRegisterResetColumnsToDefault={
              props.onRegisterResetColumnsToDefault
            }
            onUserChangedColumns={props.onUserChangedColumns}
            checkboxProps={checkboxProps}
            defaultColumnVisibilityModel={props.defaultColumnVisibilityModel}
            clientHeight={props.clientHeight}
            reportId={props.reportId}
            setSort={props.setSort}
            rowHoverActions={props.rowHoverActions}
            rowApprovalActions={props.rowApprovalActions}
            context={context}
          />
        )
      )}
      {context === TransactionReportType.NeedsApproval ? (
        <TransactionsTableNeedsApprovalBulkFloatingToolbar
          selectedTransactions={selectedTransactions}
          handleCloseToolbar={handleCloseToolbar}
          filters={props.filters}
        />
      ) : (
        <TransactionsTableBulkFloatingToolbar
          context={context}
          filters={props.filters}
          selectedTransactions={selectedTransactions}
          showSyncWithIntegration={!!user?.hasOrgBookkeeperPermissions}
          handleCloseToolbar={handleCloseToolbar}
          handleSync={(): void =>
            dispatch(createSetIsSyncTransactionsPopupOpen(true))
          }
          orgExpenseReportId={props.reportId}
        />
      )}
    </div>
  )
}

export default React.memo(FullTransactionsTable)
